(() => {
  if (customElements.get('chapon-home-motion')) return;

  class ChaponHomeMotion extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-section]');
      if (!this.root) return;
      this.controller?.abort();
      this.controller = new AbortController();
      // The skip listener lives on this controller, so wire it again after a reconnect.
      this.skipWired = false;
      const signal = this.controller.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => this.setup(), { signal });
      ['wheel', 'touchstart', 'touchmove', 'keydown'].forEach((type) => window.addEventListener(type, () => { this.lastInput = performance.now(); }, { passive: true, signal }));
      this.setup();
      window.addEventListener('scroll', () => {
        if (this.near !== false && !this.frame && !this.motion.matches) this.frame = requestAnimationFrame(() => this.drift());
      }, { passive: true, signal });
      window.addEventListener('resize', () => { this.rangeCache = null; }, { passive: true, signal });
      // Scroll work runs only while the section is within about a screen; one more pass on leaving settles the film, header and steam.
      if (!('IntersectionObserver' in window)) return;
      this.nearObserver?.disconnect();
      this.nearObserver = new IntersectionObserver(([entry]) => {
        this.near = entry.isIntersecting;
        if (!this.frame && !this.motion.matches) this.frame = requestAnimationFrame(() => this.drift());
      }, { rootMargin: '100% 0px' });
      this.nearObserver.observe(this.root);
    }

    setup() {
      this.observer?.disconnect();
      this.scrub();
      this.root.classList.remove('home-reveal-ready');
      this.steams = [...this.root.querySelectorAll('.steam, .stage__steam')];
      this.steams.forEach((image) => { image.style.transform = ''; });
      if (this.motion.matches || !('IntersectionObserver' in window)) return;
      // The meadow's scroll timelines own the concept's motion where the browser supports them.
      const meadow = this.root.matches('[data-meadow]') && CSS.supports('animation-timeline', 'view()');
      const own = (element) => !(meadow && element.closest('.concept__band, .concept'));
      const images = [...this.root.querySelectorAll('.concept__band img, .concept__photos img, .stage__photo img, .pconcept__media img')].filter(own);
      const texts = [...this.root.querySelectorAll('[data-reveal], .concept > div:first-child, .stage__text')].filter(own);
      images.forEach((image) => image.setAttribute('data-reveal-img', ''));
      texts.forEach((text) => text.setAttribute('data-reveal', ''));
      this.root.classList.add('home-reveal-ready');
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          this.observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      [...images, ...texts].forEach((element) => this.observer.observe(element));
      this.drift();
    }

    // The night film: cloned from its template about a screen before the section, then it plays on its own (muted, once, stopping on
    // its last frame) while the pinned stage is on screen; the captions follow the playback time (owner, 2026-10-14).
    // Reduced motion, Save-Data and browsers without scroll timelines keep the photo as the poster and never fetch it.
    scrub() {
      this.diveObserver?.disconnect();
      this.diveVideo?.pause();
      this.releaseDive(true);
      this.diveVideo = null;
      this.rangeCache = null;
      this.lines = null;
      this.root.classList.remove('wants-dive', 'has-dive', 'is-still', 'is-ended', 'is-receding', 'is-covered', 'is-skip-on', 'is-film-done');
      document.body.classList.remove('story-cinema', 'cinema-peek');
      const stage = this.root.querySelector('.concept__stage');
      const template = stage?.querySelector('template[data-meadow-dive]');
      if (!template || !this.root.matches('[data-meadow="dive"]') || this.motion.matches || navigator.connection?.saveData || !CSS.supports('animation-timeline', 'view()') || !('IntersectionObserver' in window)) return;
      this.root.classList.add('wants-dive');
      this.rangeCache = null;
      this.watchHeader();
      this.wireSkip();
      const ready = (video) => {
        this.diveVideo = video;
        this.root.classList.add('has-dive');
        video.addEventListener('timeupdate', () => this.captions(), { signal: this.controller.signal });
        // .is-ended shows the replay button beside the skip link until the film plays again.
        video.addEventListener('ended', () => {
          this.root.classList.add('is-ended');
          // Watched to the end (owner, 2026-10-14): glide on to where the skip link lands, unless the reader has just scrolled or left.
          if (this.filmShowing && performance.now() - (this.lastInput || -1e9) > 2000) this.skipTo(false);
        }, { signal: this.controller.signal });
        video.addEventListener('play', () => this.root.classList.remove('is-ended'), { signal: this.controller.signal });
        this.sync();
        this.assistDive();
        this.layers();
      };
      const existing = stage.querySelector('.concept__dive');
      if (existing?.readyState >= 2) return ready(existing);
      this.diveObserver = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        this.diveObserver.disconnect();
        let video = stage.querySelector('.concept__dive');
        if (!video) {
          stage.insertBefore(template.content.cloneNode(true), template);
          video = stage.querySelector('.concept__dive');
        }
        video.addEventListener('loadeddata', () => ready(video), { once: true, signal: this.controller.signal });
        // If the clip cannot load, fall back to the photo layout instead of a long scroll over a still.
        video.addEventListener('error', () => { this.diveVideo = null; this.root.classList.remove('wants-dive', 'has-dive'); document.body.classList.remove('story-cinema', 'cinema-peek'); }, { once: true, signal: this.controller.signal });
        if (video.getAttribute('src')) return;
        video.src = window.matchMedia('(max-width: 900px)').matches ? stage.dataset.diveTall : stage.dataset.diveWide;
        // iOS paints the first frame only after the muted clip has started once; rewind it unless the stage is already showing.
        video.play().then(() => { if (!this.filmShowing) { video.pause(); video.currentTime = 0; } }).catch(() => video.load());
      }, { rootMargin: '100% 0px' });
      // Arm only after the reader starts scrolling, so the clip never adds bytes to the page load itself.
      const arm = () => this.diveObserver?.observe(this.root);
      if (window.scrollY > 0) arm();
      else window.addEventListener('scroll', arm, { once: true, passive: true, signal: this.controller.signal });
    }

    // Play while the opened stage is pinned on screen, pause outside it; the scroll never moves the playhead.
    sync(travelled = this.travelled()) {
      const video = this.diveVideo;
      if (!video || this.root.classList.contains('is-still')) return;
      const { start, out, inStart } = this.ranges();
      // Once the assist has brought the film up to full (below), it plays from the start of the fade instead of waiting for --scrub-start.
      const showing = travelled >= (this.diveAssisted ? inStart : start) && travelled < out;
      // Outside the pinned stretch no caption stays on, e.g. the last line after the film has ended and the reader scrolls back up (owner, 2026-10-14).
      const changed = showing !== this.filmShowing;
      this.filmShowing = showing;
      if (changed && showing) this.captions();
      else if (changed) this.root.querySelectorAll('.concept__captions p.is-on').forEach((line) => line.classList.remove('is-on'));
      if (showing && video.paused && !video.ended) {
        // Autoplay can be refused (e.g. iOS Low Power Mode): hide the clip so the poster photo shows with the captions together, and give the
        // header back. The pinned length stays, so the page does not jump under a reader who is inside it.
        video.play().catch((error) => {
          if (error?.name !== 'NotAllowedError') return;
          this.root.classList.add('is-still');
          this.root.classList.remove('has-dive');
          this.cinema();
        });
      } else if (!showing && !video.paused) video.pause();
    }

    travelled(top = this.root.getBoundingClientRect().top) {
      return (window.innerHeight - top) / (window.innerHeight / 100);
    }

    // The film's CSS ranges in vh, read once per viewport size instead of on every scroll frame. unit is one CSS vh in px, which on iOS
    // is the tall viewport (toolbar hidden), not innerHeight / 100.
    ranges() {
      if (this.rangeCache) return this.rangeCache;
      const style = getComputedStyle(this.root), value = (name) => parseFloat(style.getPropertyValue(name));
      const start = value('--scrub-start');
      const probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;top:0;width:0;height:100vh;visibility:hidden';
      this.root.append(probe);
      const unit = probe.offsetHeight / 100;
      probe.remove();
      this.rangeCache = { start, out: start + value('--scrub-len') * (1 + (value('--scrub-hold') || 0)), inStart: value('--dive-in-start'), inEnd: value('--dive-in-end'), unit };
      return this.rangeCache;
    }

    // Smooth scrolling in in-app browsers (owner, 2026-10-27: the LINE browser stuttered over the film). The stage's recede is attached
    // only from just before its range, so the pinned stage is not drawn through an offscreen layer while the film plays; the photo is not
    // drawn while the opaque film covers it; the skip link and captions are hidden outside their ranges here instead of by visibility
    // keyframes, which kept those scroll animations on the main thread. Each class changes only where the scroll animation shows no change.
    layers(top = this.root.getBoundingClientRect().top) {
      if (!this.root.classList.contains('wants-dive')) return;
      const { out, inStart, inEnd, unit } = this.ranges(), at = (window.innerHeight - top) / unit, list = this.root.classList;
      list.toggle('is-receding', at >= out - 10);
      list.toggle('is-covered', list.contains('has-dive') && !list.contains('is-still') && at >= inEnd + 2);
      list.toggle('is-skip-on', at > inStart - 2 && at < out + 14);
      list.toggle('is-film-done', at >= out + 14);
    }

    // Fade assist (owner, 2026-10-16): the poster-to-film crossfade follows the scroll (--dive-in-start to --dive-in-end), so a reader who
    // stops inside it was left with a half-transparent mix. When the scroll rests there for 150 ms and the film can play, the film rises from
    // its current opacity to full and starts; the page itself never moves. Scrolling back above the fade hands the film back to the scroll.
    assistDive(travelled = this.travelled()) {
      clearTimeout(this.assistTimer);
      if (!this.diveVideo || !this.root.classList.contains('has-dive') || this.root.classList.contains('is-still')) return;
      if (this.diveAssisted && travelled < this.ranges().inStart) return this.releaseDive();
      this.assistTimer = setTimeout(() => {
        if (!this.diveVideo || !this.isConnected) return;
        const travelled = this.travelled(), { inStart: from, inEnd: to } = this.ranges();
        if (travelled < from) return this.releaseDive();
        if (this.diveAssisted || travelled >= to || this.diveVideo.readyState < 2) return;
        const video = this.diveVideo, opacity = getComputedStyle(video).opacity;
        this.diveAssisted = true;
        video.style.animation = 'none';
        video.style.opacity = opacity;
        void video.offsetWidth;
        video.style.transition = 'opacity .6s ease-out';
        video.style.opacity = '1';
        this.sync();
      }, 150);
    }

    releaseDive(now = false) {
      const video = this.diveVideo;
      if (!this.diveAssisted || !video) { this.diveAssisted = false; return; }
      this.diveAssisted = false;
      const clear = () => {
        if (this.diveAssisted) return;
        ['animation', 'opacity', 'transition'].forEach((property) => video.style.removeProperty(property));
      };
      if (now) return clear();
      // Above the fade the scroll timeline would show the film at 0%: fade there first, then hand the opacity back to it.
      video.style.transition = 'opacity .4s ease-in';
      video.style.opacity = '0';
      setTimeout(clear, 420);
      this.sync();
    }

    // Skip (owner, 2026-10-14): glide for about a second to where the film has just faded out, the place a reader who watched to the end
    // reaches by scrolling on. With reduced motion there is no film and the link keeps its plain jump to the Concept heading.
    wireSkip() {
      const link = this.root.querySelector('.concept__skip a');
      if (!link || this.skipWired) return;
      this.skipWired = true;
      link.addEventListener('click', (event) => {
        if (this.motion.matches || !this.root.classList.contains('wants-dive')) return;
        event.preventDefault();
        // A keyboard press (no pointer, detail 0) moves focus to the heading as the plain link would; a tap or click does not draw a focus ring.
        this.skipTo(event.detail === 0);
      }, { signal: this.controller.signal });
      // Replay (owner, 2026-10-14): from the first frame, captions included; sync() keeps it paused if the stage is no longer on screen.
      this.root.querySelector('.concept__replay')?.addEventListener('click', (event) => {
        const video = this.diveVideo;
        if (!video) return;
        // The button hides once the film plays; keep keyboard focus on the skip link beside it.
        if (event.currentTarget === document.activeElement) link.focus({ preventScroll: true });
        video.currentTime = 0;
        this.captions();
        if (this.filmShowing) video.play().catch(() => {});
      }, { signal: this.controller.signal });
    }

    skipTo(keyboard) {
      // The ranges are in CSS vh, which on iOS is the tall viewport (toolbar hidden), not innerHeight; ranges() measures it.
      const { out, unit } = this.ranges();
      // The stage stays pinned and unchanged up to --scrub-out, so start there at once; the glide then spends its second on the 85vh over
      // which the stage recedes and fades out (meadow-recede in brand-concept.css) while the Concept rises.
      const at = (vh) => this.root.getBoundingClientRect().top + window.scrollY + vh * unit - window.innerHeight;
      if (window.scrollY < at(out)) window.scrollTo({ top: at(out), behavior: 'instant' });
      this.glide(at(out + 85), () => {
        const heading = this.root.querySelector('.concept__key');
        if (!heading || !keyboard) return;
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      });
    }

    // Eased scroll that the reader can take over with the wheel, a touch or a key.
    glide(target, done) {
      const from = window.scrollY, distance = target - from, began = performance.now(), duration = 1000;
      const stop = new AbortController();
      ['wheel', 'touchstart', 'touchmove', 'keydown'].forEach((type) => window.addEventListener(type, () => stop.abort(), { passive: true, signal: stop.signal }));
      this.controller.signal.addEventListener('abort', () => stop.abort(), { signal: stop.signal });
      const step = (now) => {
        if (stop.signal.aborted) return;
        const t = Math.min(1, (now - began) / duration), eased = t < .5 ? 4 * t * t * t : 1 - (2 - 2 * t) ** 3 / 2;
        window.scrollTo({ top: from + distance * eased, behavior: 'instant' });
        if (t < 1) return requestAnimationFrame(step);
        stop.abort();
        done();
      };
      requestAnimationFrame(step);
    }

    captions() {
      const video = this.diveVideo;
      if (!video?.duration) return;
      if (!this.filmShowing) return;
      const progress = video.currentTime / video.duration;
      this.lines ??= [...this.root.querySelectorAll('.concept__captions p')].map((line) => {
        const style = getComputedStyle(line);
        return { line, from: parseFloat(style.getPropertyValue('--from')), to: parseFloat(style.getPropertyValue('--to')) };
      });
      this.lines.forEach(({ line, from, to }) => line.classList.toggle('is-on', progress >= from && progress < to));
    }

    // Film mode: from the moment the photo starts to open until the Concept arrives, the header slides away (owner, 2026-10-13).
    // It peeks back when the pointer nears the top edge, on an upward scroll, on keyboard focus or while the menu is open.
    watchHeader() {
      const header = document.querySelector('.site-header-section');
      if (!header || this.headerWatched) return;
      this.headerWatched = true;
      const signal = this.controller.signal, body = document.body;
      const peek = (on) => body.classList.toggle('cinema-peek', on);
      let lastY = window.scrollY;
      const touch = matchMedia('(hover: none)');
      window.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || !body.classList.contains('story-cinema')) return;
        const edge = header.getBoundingClientRect().height + 24;
        if (event.clientY <= 48) peek(true);
        else if (event.clientY > edge && !header.matches(':focus-within')) peek(false);
      }, { passive: true, signal });
      window.addEventListener('scroll', () => {
        const y = window.scrollY, delta = y - lastY;
        lastY = y;
        if (!touch.matches || Math.abs(delta) < 6) return;
        if (delta > 0 && header.matches(':focus-within')) return;
        peek(delta < 0);
      }, { passive: true, signal });
      header.addEventListener('focusin', () => peek(true), { signal });
      header.addEventListener('focusout', (event) => { if (!header.contains(event.relatedTarget)) peek(false); }, { signal });
    }

    cinema(travelled = this.travelled()) {
      if (!this.headerWatched) return;
      const unit = window.innerHeight / 100, end = this.ranges().out + 5;
      // On the Story page the film already sits in a phone's first screen, so the header also waits until the page has scrolled 40vh (#512).
      const on = this.root.classList.contains('wants-dive') && !this.root.classList.contains('is-still') && travelled >= 40 && window.scrollY >= 40 * unit && travelled < end && !document.body.classList.contains('menu-open');
      if (on !== document.body.classList.contains('story-cinema')) {
        document.body.classList.toggle('story-cinema', on);
        if (!on) document.body.classList.remove('cinema-peek');
      }
    }

    drift() {
      this.frame = null;
      if (this.motion.matches || !this.isConnected) return;
      const top = this.root.getBoundingClientRect().top, travelled = this.travelled(top);
      this.sync(travelled);
      this.cinema(travelled);
      this.assistDive(travelled);
      this.layers(top);
      const height = window.innerHeight;
      this.steams.forEach((steam) => {
        const rect = steam.parentElement.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > height + 200) return;
        const progress = (rect.top + rect.height / 2 - height / 2) / (height + rect.height);
        const offset = Math.max(-6, Math.min(6, progress * -12));
        steam.style.transform = `translate3d(0,${offset.toFixed(2)}%,0)`;
      });
    }

    disconnectedCallback() {
      document.body.classList.remove('story-cinema', 'cinema-peek');
      this.observer?.disconnect();
      this.diveObserver?.disconnect();
      this.nearObserver?.disconnect();
      this.near = undefined;
      this.controller?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.root?.classList.remove('home-reveal-ready');
      this.steams?.forEach((image) => { image.style.transform = ''; });
    }
  }
  customElements.define('chapon-home-motion', ChaponHomeMotion);
})();
